package com.arena_web.pages

import androidx.compose.runtime.*
import com.arena_web.data.dto.ClubCityDto
import com.arena_web.data.dto.ClubDto
import com.arena_web.data.local.ClubsCatalogCache
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.forms.DatePicker
import com.arena_web.ui.components.events.CreateEventPopup
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.components.widgets.LoadingSpinner
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.sections.arena.*
import com.arena_web.ui.sections.arena.mobile.ArenaContentMobile
import com.arena_web.ui.sections.events.EventsEmptyState
import com.arena_web.utils.LeafletMap
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.arena_web.utils.cachedCityCountryCode
import com.arena_web.utils.countryCodeToFlagEmoji
import com.arena_web.utils.flagAssetUrl
import com.arena_web.utils.getIpLocation
import com.arena_web.utils.getUserCoordinates
import com.arena_web.utils.lookupCityCountryCode
import com.arena_web.utils.reverseGeocodeCity
import com.arena_web.utils.searchCities
import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.roundToInt
import kotlin.math.sin
import kotlin.math.sqrt
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.graphics.Color as KobwebColor
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.arena_web.ui.localization.AppStrings
import kotlin.math.abs
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.w3c.dom.HTMLElement
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.jetbrains.compose.web.css.*
import org.w3c.dom.url.URLSearchParams

private const val PAGE_SIZE = 10

@Page("/arena")
@Composable
fun ArenaPage() {
    val t = LocalAppStrings.current
    val container = LocalAppContainer.current
    val ctx = rememberPageContext()

    // Tab state — restore from cache ("Clubs" or "Events"); Events is the
    // default landing tab.
    var activeTab by remember {
        mutableStateOf(Storage.get(StorageKeys.ARENA_TAB) ?: "Events")
    }
    val onTabSelected: (String) -> Unit = {
        activeTab = it
        Storage.save(StorageKeys.ARENA_TAB, it)
    }

    var allEvents by remember { mutableStateOf<List<ArenaEvent>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var isLoadingMore by remember { mutableStateOf(false) }
    var currentPage by remember { mutableStateOf(0) }
    var hasMore by remember { mutableStateOf(true) }
    var showCreatePopup by remember { mutableStateOf(false) }
    // Map mode — the MapPopup modal re-homed into the page itself (mobile
    // ArenaTabScreen parity): the list/grid hides and the Leaflet map fills the
    // content area as a background layer while the action bar + buttons stay on
    // top. Persisted so it survives a reload.
    var showMap by remember { mutableStateOf(Storage.getBoolean(StorageKeys.ARENA_MAP_MODE)) }
    val onToggleMap: () -> Unit = {
        showMap = !showMap
        Storage.saveBoolean(StorageKeys.ARENA_MAP_MODE, showMap)
    }
    var showAuthGate by remember { mutableStateOf(false) }
    var reloadTrigger by remember { mutableStateOf(0) }
    val isLoggedIn = Storage.get(StorageKeys.ACCESS_TOKEN) != null

    // Full club catalog — loaded (cached) so the map can render club pins and
    // the city filter can include club cities. Mirrors the mobile catalog.
    // Starts empty; the Cache API copy lands via the LaunchedEffect below
    // (the Cache API is async, unlike the old localStorage read).
    var clubCatalog by remember { mutableStateOf<List<ClubDto>>(emptyList()) }
    // Lightweight city index (city + country + count) from GET /public/clubs/cities.
    // Authoritative source for the filter dropdown's cities and flags — arrives
    // fast (no full-catalog fetch, no per-city Nominatim geocoding).
    var cityCatalog by remember { mutableStateOf<List<ClubCityDto>>(emptyList()) }
    // User coordinates for club distance labels on the map / cards.
    var userCoords by remember { mutableStateOf<Pair<Double, Double>?>(null) }

    // Clubs state (disabled)
    // var allClubs by remember { mutableStateOf<List<ClubDto>>(emptyList()) }
    // var isLoadingClubs by remember { mutableStateOf(false) }
    // var isLoadingMoreClubs by remember { mutableStateOf(false) }
    // var currentClubsPage by remember { mutableStateOf(0) }
    // var hasMoreClubs by remember { mutableStateOf(true) }

    // City filter state — restore from cache immediately
    // "__all__" means user explicitly chose "All Cities"; null means no choice yet
    val cachedCity = remember { Storage.get(StorageKeys.SELECTED_CITY) }
    var availableCities by remember { mutableStateOf<List<String>>(emptyList()) }
    var cityCountryCodes by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var selectedCity by remember { mutableStateOf(if (cachedCity == "__all__") null else cachedCity) }
    var userCity by remember { mutableStateOf<String?>(null) }
    var citiesReady by remember { mutableStateOf(cachedCity != null) }

    // SEO landing pages link here as /arena?city=<name> or /arena?sport=<ENUM>.
    // Preset the existing filters once on entry (additive; no effect when absent).
    LaunchedEffect(Unit) {
        val params = URLSearchParams(window.location.search)
        var applied = false
        params.get("sport")?.takeIf { it.isNotBlank() }?.let { sport ->
            Storage.save(StorageKeys.SELECTED_SPORT_NAME, sport.uppercase())
            activeTab = "Clubs"
            applied = true
        }
        params.get("city")?.takeIf { it.isNotBlank() }?.let { city ->
            selectedCity = city
            applied = true
        }
        if (applied) reloadTrigger++
    }

    // Community filter removed from the Arena UI — any selection persisted by
    // older builds is dropped so it can't keep filtering events invisibly.
    LaunchedEffect(Unit) {
        Storage.remove(StorageKeys.SELECTED_COMMUNITY_ID)
        Storage.remove(StorageKeys.SELECTED_COMMUNITY_NAME)
    }

    // Date filter — restore from cache; the sentinel "__all__" means the user
    // explicitly cleared the filter, null means "first visit → default to
    // today" so we don't dump every event including past ones.
    var selectedDate by remember {
        val now = kotlin.js.Date()
        val y = now.getFullYear()
        val m = (now.getMonth() + 1).toString().padStart(2, '0')
        val d = now.getDate().toString().padStart(2, '0')
        val today = "$y-$m-$d"
        val cached = Storage.get(StorageKeys.SELECTED_DATE)
        val initial = when {
            cached == null -> today
            cached == "__all__" -> null
            // A stale past date (e.g. saved yesterday) is no longer selectable —
            // fall back to today and refresh the cache.
            cached < today -> {
                Storage.save(StorageKeys.SELECTED_DATE, today)
                today
            }
            else -> cached
        }
        mutableStateOf<String?>(initial)
    }

    val scope = rememberCoroutineScope()

    // Build API params from current filters
    fun buildParams(page: Int): Map<String, String> = buildMap {
        put("page", page.toString())
        put("size", PAGE_SIZE.toString())
        val sportId = Storage.get(StorageKeys.SELECTED_SPORT_ID) ?: ""
        if (sportId.isNotBlank()) put("sportId", sportId)
        if (selectedCity != null) put("city", selectedCity!!)
        if (selectedDate != null) put("date", selectedDate!!)
    }

    fun sortEvents(events: List<ArenaEvent>): List<ArenaEvent> = events.sortedBy { event ->
        when (event.status) {
            EventStatus.IN_PROGRESS -> 0
            EventStatus.NEW -> 1
            EventStatus.COMPLETED -> 2
            EventStatus.CANCELED -> 3
        }
    }

    // Initial load + reload on filter/sport changes
    var isFirstLoad by remember { mutableStateOf(true) }
    LaunchedEffect(reloadTrigger, selectedCity, selectedDate) {
        // A filter change means the next mount shouldn't restore the previous
        // scroll position (it belongs to the previous result set). The very
        // first effect run is the page-mount load — keep the saved value so
        // returning from event details still restores correctly.
        if (!isFirstLoad) {
            kotlinx.browser.window.sessionStorage.removeItem("arena_mobile_events_scroll_top")
            kotlinx.browser.window.sessionStorage.removeItem("arena_desktop_events_scroll_top")
        }
        isFirstLoad = false
        isLoading = true
        currentPage = 0
        hasMore = true
        val result = container.publicEventRepository.getEvents(buildParams(0))
        result.onSuccess { page ->
            allEvents = sortEvents(page.events.map { it.toArenaEvent() })
            hasMore = page.events.size >= PAGE_SIZE
            currentPage = 0
        }.onFailure {
            console.error("Failed to load events: ${it.message}")
        }
        isLoading = false
    }

    // Load next page
    val loadMore: () -> Unit = {
        if (hasMore && !isLoadingMore && !isLoading) {
            val nextPage = currentPage + 1
            isLoadingMore = true
            scope.launch {
                val result = container.publicEventRepository.getEvents(buildParams(nextPage))
                result.onSuccess { page ->
                    val newEvents = page.events.map { it.toArenaEvent() }
                    allEvents = sortEvents(allEvents + newEvents)
                    currentPage = nextPage
                    hasMore = newEvents.size >= PAGE_SIZE
                }.onFailure {
                    console.error("Failed to load more events: ${it.message}")
                }
                isLoadingMore = false
            }
        }
    }

    // Build club params with city + sport filter (disabled)
    // fun buildClubParams(page: Int): Map<String, String> = buildMap {
    //     put("page", page.toString())
    //     put("size", PAGE_SIZE.toString())
    //     val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME) ?: ""
    //     if (sportName.isNotBlank()) put("type", sportName.uppercase())
    //     if (selectedCity != null) put("city", selectedCity!!)
    // }

    // Load clubs when tab switches to "Clubs", city or sport changes (disabled)
    // LaunchedEffect(activeTab, selectedCity, reloadTrigger) {
    //     if (activeTab == "Clubs") {
    //         isLoadingClubs = true
    //         currentClubsPage = 0
    //         hasMoreClubs = true
    //         container.clubPublicRepository.browseClubs(buildClubParams(0))
    //             .onSuccess { page ->
    //                 allClubs = page.clubs
    //                 hasMoreClubs = page.clubs.size >= PAGE_SIZE
    //                 currentClubsPage = 0
    //             }
    //             .onFailure {
    //                 console.error("Failed to load clubs: ${it.message}")
    //             }
    //         isLoadingClubs = false
    //     }
    // }

    // Load next page of clubs (disabled)
    // val loadMoreClubs: () -> Unit = {
    //     if (hasMoreClubs && !isLoadingMoreClubs && !isLoadingClubs) {
    //         val nextPage = currentClubsPage + 1
    //         isLoadingMoreClubs = true
    //         scope.launch {
    //             container.clubPublicRepository.browseClubs(buildClubParams(nextPage))
    //                 .onSuccess { page ->
    //                     allClubs = allClubs + page.clubs
    //                     currentClubsPage = nextPage
    //                     hasMoreClubs = page.clubs.size >= PAGE_SIZE
    //                 }
    //                 .onFailure {
    //                     console.error("Failed to load more clubs: ${it.message}")
    //                 }
    //             isLoadingMoreClubs = false
    //         }
    //     }
    // }

    // Load cities and detect geolocation in parallel, then build the list
    LaunchedEffect(reloadTrigger) {
        // Launch geo detection and city fetching concurrently
        var detected: String? = null
        val geoJob = launch {
            // GPS when permitted, IP-derived otherwise (same fallback the
            // mobile Arena tab uses) — a denied prompt must not leave the
            // city undetected.
            val pt = getUserCoordinates()?.let { it.latitude to it.longitude }
                ?: getIpLocation()?.let { it.lat to it.lng }
            if (pt != null) {
                userCoords = pt
                detected = reverseGeocodeCity(pt.first, pt.second)
            }
        }

        // Lightweight cities endpoint — returns distinct city names for today's
        // and upcoming events only (no past-day clutter), much cheaper than
        // fetching 200 full event DTOs just to extract a string list.
        // Sport-agnostic: pull cities across every sport (including "Other"
        // custom-sport events) so switching sport doesn't empty the dropdown.
        val eventCities = container.publicEventRepository
            .getUpcomingCities(sportId = null)
            .getOrNull() ?: emptyList()

        // Cities derived from the club catalog (parsed out of each club's
        // address). Merged with the event cities so the filter covers venues
        // that have clubs but no upcoming events yet.
        val clubCities = clubCatalog.mapNotNull { clubGroupCity(it).takeIf { c -> c.isNotBlank() } }

        // Wait for geo to finish
        geoJob.join()
        userCity = detected

        // Build sorted list: geo city first, then the rest. Union events ∪ clubs
        // ∪ whatever already landed (the /cities index can arrive before geo
        // finishes — a plain assignment here used to wipe it until a refresh),
        // deduped case-insensitively (first spelling wins).
        val allCities = (eventCities + clubCities + availableCities + cityCatalog.map { it.city })
            .filter { it.isNotBlank() }
            .distinctBy { it.lowercase() }
            .sorted()
        val geoCity = detected
        availableCities = if (geoCity != null) {
            val match = allCities.find { it.equals(geoCity, ignoreCase = true) }
            val name = match ?: geoCity
            listOf(name) + allCities.filter { !it.equals(geoCity, ignoreCase = true) }
        } else {
            allCities
        }

        // The geo city is only pinned first in the list — never auto-selected.
        // Default stays "All Cities"; SELECTED_CITY is persisted solely when
        // the user explicitly picks a city from the filter.

        // Migration: a cached city may no longer match any known entry (older
        // builds auto-selected the geo city and geocoded it in the local
        // language, e.g. "Київ"). Bump it onto the detected geo city when that
        // is a known entry; otherwise drop back to "All Cities" and forget it.
        val cached = selectedCity
        if (cached != null && cached != "__all__" &&
            availableCities.isNotEmpty() &&
            availableCities.none { it.equals(cached, ignoreCase = true) }
        ) {
            val replacement = geoCity?.takeIf { match ->
                availableCities.any { it.equals(match, ignoreCase = true) }
            }
            if (replacement != null) {
                selectedCity = replacement
                Storage.save(StorageKeys.SELECTED_CITY, replacement)
            } else {
                selectedCity = null
                Storage.remove(StorageKeys.SELECTED_CITY)
            }
        }
        citiesReady = true
    }

    // Load the full club catalog once (cached copy renders near-instantly on
    // entry; the network fetch then refreshes it). Pull a large page so the
    // whole catalog (now multi-country, hundreds of clubs) loads — a 200 cap
    // silently dropped most cities from the filter/map.
    LaunchedEffect(Unit) {
        ClubsCatalogCache.load()?.takeIf { it.isNotEmpty() }?.let { cached ->
            // The network response may have raced the cache read — never
            // overwrite fresh data with the stale copy.
            if (clubCatalog.isEmpty()) clubCatalog = cached
        }
        // runCatching: a dead network here throws out of the repository (it
        // doesn't wrap fetch failures), and an uncaught throw would cancel the
        // whole page scope — on flaky mobile connections the cached catalog
        // must keep the page alive.
        runCatching {
            container.clubPublicRepository
                .browseClubs(mapOf("page" to "0", "size" to "5000"))
                .onSuccess { page ->
                    clubCatalog = page.clubs
                    ClubsCatalogCache.save(page.clubs)
                }
        }
    }

    // Lightweight city index — arrives well before the 5000-club catalog, so
    // the dropdown fills (with correct flags) almost immediately.
    LaunchedEffect(Unit) {
        container.clubPublicRepository.getCities()
            .onSuccess { cityCatalog = it.cities.filter { c -> c.city.isNotBlank() } }
    }

    // Union the endpoint's cities into the dropdown as soon as they land.
    // Also keyed on availableCities so the union re-applies if another effect
    // rebuilds the list (first-visit race: geo effect finishing after /cities).
    LaunchedEffect(cityCatalog, availableCities) {
        if (cityCatalog.isEmpty()) return@LaunchedEffect
        val existing = availableCities.map { it.lowercase() }.toSet()
        val extra = cityCatalog
            .map { it.city }
            .filter { it.lowercase() !in existing }
            .distinctBy { it.lowercase() }
        if (extra.isNotEmpty()) {
            availableCities = (availableCities + extra).distinctBy { it.lowercase() }
        }
    }

    // When the catalog loads after the city list was built, union in any club
    // cities the event list didn't already cover (sort order is applied by
    // `sortedCities`, so only membership matters here). Keyed on availableCities
    // too — see the /cities union above.
    LaunchedEffect(clubCatalog, availableCities) {
        if (clubCatalog.isEmpty()) return@LaunchedEffect
        val existing = availableCities.map { it.lowercase() }.toSet()
        val extra = clubCatalog
            .mapNotNull { clubGroupCity(it).takeIf { c -> c.isNotBlank() } }
            .filter { it.lowercase() !in existing }
            .distinctBy { it.lowercase() }
        if (extra.isNotEmpty()) {
            availableCities = (availableCities + extra).distinctBy { it.lowercase() }
        }
    }

    // Country code lookup for the city dropdown. Hits a synchronous localStorage
    // cache first (instant on repeat visits), then resolves new cities via
    // Nominatim sequentially so we stay polite about the rate limit.
    LaunchedEffect(availableCities) {
        if (availableCities.isEmpty()) {
            if (cityCountryCodes.isNotEmpty()) cityCountryCodes = emptyMap()
            return@LaunchedEffect
        }
        val initial = mutableMapOf<String, String>()
        availableCities.forEach { city ->
            cachedCityCountryCode(city)?.let { initial[city] = it }
        }
        if (initial.isNotEmpty()) cityCountryCodes = initial.toMap()
        for (city in availableCities) {
            if (city in initial) continue
            val code = lookupCityCountryCode(city) ?: continue
            initial[city] = code
            cityCountryCodes = initial.toMap()
        }
    }

    // City → ISO country code, most authoritative source last (map merge lets it
    // win): Nominatim (event-only cities) → club-catalog data → the /cities
    // endpoint. All three key on the canonical English city label.
    val cityCodes = remember(cityCatalog, clubCatalog, cityCountryCodes) {
        val fromClubs = clubCatalog.groupBy { clubGroupCity(it) }
            .mapNotNull { (city, list) ->
                list.firstNotNullOfOrNull { clubCountryOf(it) }?.let { city to it }
            }
            .toMap()
        val fromEndpoint = cityCatalog.mapNotNull { c ->
            c.country?.takeIf { it.isNotBlank() }?.lowercase()?.let { c.city to it }
        }.toMap()
        cityCountryCodes + fromClubs + fromEndpoint
    }

    // Sorted view of cities for the filter dropdown: geo-detected city stays
    // pinned to the top, then Spain, then other known countries grouped by ISO
    // code, then cities whose country couldn't be resolved.
    val sortedCities = remember(availableCities, cityCodes, userCity) {
        val geo = userCity?.lowercase()
        availableCities.sortedWith(
            compareBy(
                { if (geo != null && it.equals(userCity, ignoreCase = true)) 0 else 1 },
                {
                    val code = cityCodes[it]
                    when {
                        code == "es" -> 0
                        code != null -> 1
                        else -> 2
                    }
                },
                { cityCodes[it] ?: "zzz" },
                { it.lowercase() }
            )
        )
    }
    // Flags are bundled local SVGs (`/flags/{cc}.svg`) keyed by the resolved
    // country code — they render identically on every platform (no emoji, no
    // Nominatim). Value is the asset URL; the dropdown renders it as an <img>.
    val cityFlags = remember(cityCodes) {
        cityCodes.mapValues { flagAssetUrl(it.value) }.filterValues { it.isNotEmpty() }
    }

    // All filtering is now server-side
    val displayedEvents = allEvents

    val mobile = isMobile()
    val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all"
    // Custom sub-filter under "Other" (customSportName); blank = all Other.
    val otherSport = Storage.get(StorageKeys.SELECTED_OTHER_SPORT)?.takeIf { it.isNotBlank() }

    // Clubs come from the already-loaded full catalog (no paging), filtered
    // client-side by the selected sport + city — mirrors the mobile ViewModel's
    // publishClubs(): type is an exact enum match, city a case-insensitive
    // substring of the address.
    val displayedClubs = remember(clubCatalog, selectedCity, sportName, otherSport) {
        val typeFilter = sportName.uppercase().takeIf {
            it in setOf("FOOTBALL", "BASKETBALL", "TENNIS", "PADEL", "VOLLEYBALL", "OTHER")
        }
        val city = selectedCity
        clubCatalog.filter { club ->
            // Multi-sport aware: the club passes when the selected sport is ANY
            // of its types (older payloads have an empty list — primary only).
            (typeFilter == null || typeFilter in club.types.ifEmpty { listOf(club.type) }) &&
                // Under "Other", narrow to a specific custom sport when picked.
                (typeFilter != "OTHER" || otherSport == null ||
                    club.customSportName.equals(otherSport, ignoreCase = true)) &&
                // Group by province: a club matches the selected city when its
                // province (postcode-derived) equals it, so metro suburbs stay
                // under their capital while keeping their real address.
                (city.isNullOrBlank() || clubGroupCity(club).equals(city, ignoreCase = true))
        }
    }
    // Incremental LIST rendering: with "All cities" the filter holds thousands
    // of clubs — mounting every card at once froze the page for seconds. The
    // grids already fire onLoadMore near scroll-bottom, so feed them pages of
    // CLUB_LIST_PAGE. The MAP intentionally keeps the full displayedClubs.
    var visibleClubCount by remember { mutableStateOf(CLUB_LIST_PAGE) }
    LaunchedEffect(displayedClubs) { visibleClubCount = CLUB_LIST_PAGE }
    val visibleClubs = remember(displayedClubs, visibleClubCount) {
        displayedClubs.take(visibleClubCount)
    }
    val hasMoreClubs = visibleClubCount < displayedClubs.size
    val loadMoreClubs: () -> Unit = {
        if (visibleClubCount < displayedClubs.size) {
            visibleClubCount = (visibleClubCount + CLUB_LIST_PAGE).coerceAtMost(displayedClubs.size)
        }
    }
    // In map mode drop the sport ambient image so the page + top bar fall back
    // to the plain body background (like the Chat screen) — the map and its
    // top gradient then blend into a clean backdrop instead of the sport art.
    val backgroundImage = if (showMap) null else AppIcons.statisticBackground(sportName)

    AppPageLayout(
        activeTab = SidebarTab.Arena,
        title = t.pageArenaSeoTitle,
        description = t.pageArenaSeoDescription,
        onSportChanged = { reloadTrigger++ },
        backgroundImage = backgroundImage,
        mobileReady = mobile,
        // Arena swaps the bell for the activity (pulse) action — the mobile
        // counterpart of the desktop "My Activity" header button.
        mobileHeaderAction = com.arena_web.ui.sections.header.MobileHeaderAction.Activity,
        // Arena's trailing slot is the chat shortcut (the avatar moved to the
        // Profile page).
        mobileHeaderTrailing = com.arena_web.ui.sections.header.MobileHeaderTrailing.Chat,
    ) {
        if (mobile) {
          Box(
              modifier = Modifier
                  .fillMaxSize()
                  .position(Position.Relative)
          ) {
            // Background map layer (in-page map mode) — sits behind the content
            // and shows pins for the active tab; the action bar stays live on
            // top and the list/grid hides when showMap is on.
            if (showMap) {
                ArenaBackgroundMap(
                    activeTab = activeTab,
                    events = displayedEvents,
                    clubs = displayedClubs,
                    userLat = userCoords?.first,
                    userLng = userCoords?.second,
                    selectedCity = selectedCity,
                    cityFlags = cityFlags,
                    sportName = sportName,
                    fullBleed = true,
                    cullMarkers = true,
                    onEventOpen = { event -> ctx.router.navigateTo(Routes.eventDetails(event.id)) },
                    onClubOpen = { club -> ctx.router.navigateTo(Routes.clubDetail(club.id)) }
                )
            }
            ArenaContentMobile(
                activeTab = activeTab,
                onTabSelected = onTabSelected,
                events = displayedEvents,
                isLoading = isLoading,
                isLoadingMore = isLoadingMore,
                onAddEvent = {
                    if (isLoggedIn) showCreatePopup = true else showAuthGate = true
                },
                onEventClick = { event ->
                    ctx.router.navigateTo(Routes.eventDetails(event.id))
                },
                onLoadMore = loadMore,
                onMapClick = onToggleMap,
                showMap = showMap,
                clubs = visibleClubs,
                isLoadingClubs = false,
                isLoadingMoreClubs = hasMoreClubs,
                onLoadMoreClubs = loadMoreClubs,
                onClubClick = { club -> ctx.router.navigateTo(Routes.clubDetail(club.id)) },
                userLat = userCoords?.first,
                userLng = userCoords?.second,
                filterCount = 0,
                onFilterClick = {},
                sportName = sportName,
                cities = sortedCities,
                cityFlags = cityFlags,
                selectedCity = selectedCity,
                onCitySelected = {
                    selectedCity = it
                    if (it != null) Storage.save(StorageKeys.SELECTED_CITY, it)
                    else Storage.save(StorageKeys.SELECTED_CITY, "__all__")
                },
                selectedDate = selectedDate,
                onDateSelected = {
                    selectedDate = it
                    if (it != null) Storage.save(StorageKeys.SELECTED_DATE, it)
                    else Storage.save(StorageKeys.SELECTED_DATE, "__all__")
                },
            )
          }
        } else {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .position(Position.Relative)
            ) {
            // Background map layer (in-page map mode) — behind the action bar +
            // list/grid, showing pins for the active tab. The action bar stays
            // live on top and the list/grid hides when showMap is on.
            if (showMap) {
                ArenaBackgroundMap(
                    activeTab = activeTab,
                    events = displayedEvents,
                    clubs = displayedClubs,
                    userLat = userCoords?.first,
                    userLng = userCoords?.second,
                    selectedCity = selectedCity,
                    cityFlags = cityFlags,
                    sportName = sportName,
                    underTopBar = true,
                    onEventOpen = { event -> ctx.router.navigateTo(Routes.eventDetails(event.id)) },
                    onClubOpen = { club -> ctx.router.navigateTo(Routes.clubDetail(club.id)) }
                )
            }
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .position(Position.Relative)
                    // Above the map's stacking context (map layer is z-index 0)
                    // so the action bar + buttons stay visible and clickable.
                    .zIndex(1)
                    .sPadding(26, 40)
                    .sGap(24)
                    // In map mode this overlay must be transparent to pointer
                    // events on its empty areas so drag/zoom/tap fall through to
                    // Leaflet; the controls inside re-enable pointer-events.
                    .styleModifier {
                        if (showMap) property("pointer-events", "none")
                    }
            ) {
                ArenaActionBar(
                    activeTab = activeTab,
                    onTabSelected = onTabSelected,
                    onAddEvent = {
                    if (isLoggedIn) showCreatePopup = true else showAuthGate = true
                },
                    onAddClub = { /* container.requestBusinessSwitchHighlight() */ },
                    onMapClick = onToggleMap,
                    showMap = showMap,
                    cities = sortedCities,
                    cityFlags = cityFlags,
                    selectedCity = selectedCity,
                    onCitySelected = {
                        selectedCity = it
                        if (it != null) Storage.save(StorageKeys.SELECTED_CITY, it)
                        else Storage.save(StorageKeys.SELECTED_CITY, "__all__")
                    },
                    citiesReady = citiesReady,
                    selectedDate = selectedDate,
                    onDateSelected = {
                    selectedDate = it
                    if (it != null) Storage.save(StorageKeys.SELECTED_DATE, it)
                    else Storage.save(StorageKeys.SELECTED_DATE, "__all__")
                }
                )

                // Map mode hides the list/grid — the background map is the
                // browsing surface; the action bar above stays live.
                if (!showMap) {
                    if (activeTab == "Events") {
                        if (isLoading) {
                            Box(
                                modifier = Modifier.fillMaxWidth().flexGrow(1),
                                contentAlignment = Alignment.Center,
                            ) {
                                // Intentionally empty — no loading indicator (user pref).
                            }
                        } else if (displayedEvents.isEmpty()) {
                            EmptyEventsState(onAddEvent = {
                                if (isLoggedIn) showCreatePopup = true else showAuthGate = true
                            })
                        } else {
                            EventGrid(
                                events = displayedEvents,
                                isLoadingMore = isLoadingMore,
                                onEventClick = { event ->
                                    ctx.router.navigateTo(Routes.eventDetails(event.id))
                                },
                                onLoadMore = loadMore,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .flexGrow(1)
                                    .sPaddingRight(8)
                                    .styleModifier {
                                        property("min-height", "0")
                                        property("overflow-y", "auto")
                                        property("overflow-x", "hidden")
                                        property("scrollbar-width", "thin")
                                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                                    }
                            )
                        }
                    } else {
                        // Clubs tab — full catalog filtered client-side by
                        // city + sport (no paging); reuses the desktop ClubGrid.
                        if (displayedClubs.isEmpty()) {
                            EmptyClubsState()
                        } else {
                            ClubGrid(
                                clubs = visibleClubs,
                                isLoadingMore = hasMoreClubs,
                                onClubClick = { club ->
                                    ctx.router.navigateTo(Routes.clubDetail(club.id))
                                },
                                onLoadMore = loadMoreClubs,
                                userLat = userCoords?.first,
                                userLng = userCoords?.second,
                                selectedCity = selectedCity,
                                cityFlags = cityFlags,
                                sportName = sportName,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .flexGrow(1)
                                    .sPaddingRight(8)
                                    .styleModifier {
                                        property("min-height", "0")
                                        property("overflow-y", "auto")
                                        property("overflow-x", "hidden")
                                        property("scrollbar-width", "thin")
                                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                                    }
                            )
                        }
                    }
                }
            }
          }
        }

        // Popup lives inside the layout so it inherits the LocalSportColors
        // CompositionLocal (provided by AppPageLayout).
        CreateEventPopup(
            isOpen = showCreatePopup,
            onClose = { showCreatePopup = false },
            onCreated = {
                showCreatePopup = false
                reloadTrigger++
            }
        )

        com.arena_web.ui.components.overlay.AccountRequiredModal(
            isOpen = showAuthGate,
            onClose = { showAuthGate = false },
            onSignedIn = {
                showAuthGate = false
                kotlinx.browser.window.location.reload()
            }
        )
    }
}

// =============================================================================
// Action Bar
// =============================================================================

@Composable
private fun ArenaActionBar(
    activeTab: String = "Events",
    onTabSelected: (String) -> Unit = {},
    onAddEvent: () -> Unit = {},
    onAddClub: () -> Unit = {},
    onMapClick: () -> Unit = {},
    showMap: Boolean = false,
    cities: List<String> = emptyList(),
    cityFlags: Map<String, String> = emptyMap(),
    selectedCity: String? = null,
    onCitySelected: (String?) -> Unit = {},
    citiesReady: Boolean = false,
    selectedDate: String? = null,
    onDateSelected: (String?) -> Unit = {}
) {
    // z-index lifts the entire bar into its own stacking context so the
    // filter dropdowns (which overflow below the bar) aren't covered by
    // the EventGrid that follows in the parent Column.
    // In map mode the row itself is transparent to pointer events (so map
    // drag/zoom/tap fall through in the gaps between controls); each control
    // group below re-enables pointer-events so it stays clickable.
    Box(
        modifier = Modifier.fillMaxWidth().position(Position.Relative).zIndex(50)
            .styleModifier { if (showMap) property("pointer-events", "none") }
    ) {
        // Left: Clubs | Events switch, anchored to the leading edge.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("left", "0")
                property("top", "50%")
                property("transform", "translateY(-50%)")
                if (showMap) property("pointer-events", "auto")
            }
        ) {
            ClubsEventsSwitch(
                activeTab = activeTab,
                onTabSelected = onTabSelected
            )
        }

        // Centered filter pills — absolutely positioned so the right-side
        // action buttons don't push them off-axis.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("left", "50%")
                property("top", "50%")
                property("transform", "translate(-50%, -50%)")
                if (showMap) property("pointer-events", "auto")
            }
        ) {
            ActionBarFilterPills(
                activeTab = activeTab,
                cities = cities,
                cityFlags = cityFlags,
                selectedCity = selectedCity,
                onCitySelected = onCitySelected,
                citiesReady = citiesReady,
                selectedDate = selectedDate,
                onDateSelected = onDateSelected
            )
        }

        // Right: action buttons, anchored to the trailing edge.
        Row(
            modifier = Modifier.sGap(8).styleModifier {
                property("position", "absolute")
                property("right", "0")
                property("top", "50%")
                property("transform", "translateY(-50%)")
                if (showMap) property("pointer-events", "auto")
            },
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Add is events-only — there is nothing to create on the Clubs tab.
            if (activeTab == "Events") {
                AddEventButton(onClick = onAddEvent)
            }
            MapButton(onClick = onMapClick, showMap = showMap)
        }

        // Spacer establishes row height to match the action button pill.
        Box(modifier = Modifier.sHeight(48))
    }
}

@Composable
private fun ClubsEventsSwitch(activeTab: String = "Events", onTabSelected: (String) -> Unit = {}) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .sHeight(44)
            .sBorderRadius(34)
            .sPadding(2, 4)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property("background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                    "linear-gradient(0deg, var(--l-s3, #060E1E), var(--l-s3, #060E1E)) padding-box, " +
                    "${AppGradients.ButtonBorderGradientPrimary} border-box")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Clubs first (left) on desktop; the mobile switch mirrors this order.
        listOf("Clubs", "Events").forEach { tab ->
            val isActive = tab == activeTab
            Box(
                modifier = Modifier
                    .sHeight(36)
                    .sBorderRadius(22)
                    .sPadding(8, 26)
                    .then(
                        if (isActive) Modifier.backgroundColor(sportColors.primary)
                        else Modifier
                    )
                    .cursor(Cursor.Pointer)
                    .onClick { onTabSelected(tab) },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    if (tab == "Events") t.pageEventsTab else t.pageClubsTab,
                    modifier = Modifier
                        .bodyLMedium()
                        .color(if (isActive) org.jetbrains.compose.web.css.Color(sportColors.onPrimary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }
    }
}

// =============================================================================
// Inline Filter Pills (center of action bar)
// =============================================================================

@Composable
private fun ActionBarFilterPills(
    activeTab: String,
    cities: List<String>,
    cityFlags: Map<String, String> = emptyMap(),
    selectedCity: String?,
    onCitySelected: (String?) -> Unit,
    citiesReady: Boolean,
    selectedDate: String? = null,
    onDateSelected: (String?) -> Unit = {}
) {
    val t = LocalAppStrings.current
    // Figma 8438:274715 — 2px vertical padding, 8px gap; each pill sits on its
    // own translucent navy underlay with the project gradient border. The row
    // itself is transparent: the old var(--l-s3, #02040b) outer surface was invisible on
    // the page background but read as a dark strip between the pills once the
    // map started running underneath the bar.
    Row(
        modifier = Modifier
            .sBorderRadius(100)
            .sPadding(2, 0)
            .sGap(8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Location — always visible, shows "All cities" until cities load.
        // Uses the sport-style panel (round flag chips + globe for "All cities").
        FilterPillDropdown(
            label = t.pageFilterLocation,
            options = cities,
            optionPrefixes = cities.map { cityFlags[it].orEmpty() },
            selectedValue = selectedCity,
            allLabel = t.dropdownAllCities,
            onSelected = onCitySelected,
            useCityPanel = true
        )

        // Events-only filters
        if (activeTab == "Events") {
            FilterPillDate(
                label = t.pageFilterCalendar,
                selectedDate = selectedDate,
                onDateSelected = onDateSelected
            )
        }
    }
}

@Composable
private fun FilterPillDropdown(
    label: String,
    options: List<String>,
    values: List<String>? = null,
    optionPrefixes: List<String>? = null,
    selectedValue: String?,
    allLabel: String,
    onSelected: (String?) -> Unit,
    onOpen: () -> Unit = {},
    // When true, the expanded menu renders the sport-style LocationDropdownPanel
    // (round flag chips + globe "All cities" row) instead of the plain text list.
    useCityPanel: Boolean = false,
) {
    val sportColors = LocalSportColors.current
    var expanded by remember { mutableStateOf(false) }
    val selectedIndex = if (values != null) values.indexOf(selectedValue)
        else options.indexOf(selectedValue)
    val selectedLabel = if (selectedIndex >= 0) options[selectedIndex] else null
    val selectedPrefix = optionPrefixes?.getOrNull(selectedIndex).orEmpty()

    // Stable DOM id so we can detect clicks outside this dropdown — the
    // fixed-position backdrop trick fails here because the action bar's
    // ancestor uses `transform`, which traps `position: fixed` inside it.
    val dropdownId = remember { "fpd-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    // -1 = nothing hovered, -2 = "All" option, >= 0 = options[i]
    var hoveredIndex by remember { mutableStateOf(-1) }

    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(dropdownId)
            if (target != null && root != null && !root.contains(target)) {
                expanded = false
            }
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose {
            kotlinx.browser.document.removeEventListener("mousedown", handler)
        }
    }

    Box(modifier = Modifier.id(dropdownId).position(Position.Relative)) {
        // Pill trigger — Figma 9508:162429, h 44, px 22, py 10.
        Box(
            modifier = Modifier
                .sHeight(44)
                .sBorderRadius(100)
                .sPadding(10, 22)
                .cursor(Cursor.Pointer)
                .onClick {
                    val nowOpen = !expanded
                    expanded = nowOpen
                    if (nowOpen) onOpen()
                }
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property("background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--l-s2, #0B1628), var(--l-s2, #0B1628)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box")
                },
            contentAlignment = Alignment.Center
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "$label: ",
                    modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier { property("white-space", "nowrap") }
                )
                if (selectedLabel != null && selectedPrefix.isNotEmpty()) {
                    if (useCityPanel) {
                        // selectedPrefix is a local flag SVG URL — emoji-style
                        // 4:3 rounded-rect badge (full flag, no crop).
                        Box(
                            modifier = Modifier
                                .sWidth(20)
                                .sHeight(15)
                                .sMarginRight(6)
                                .styleModifier {
                                    property("background-image", "url(\"$selectedPrefix\")")
                                    property("background-size", "cover")
                                    property("background-position", "center")
                                    property("border-radius", "3px")
                                }
                        )
                    } else {
                        SpanText(
                            selectedPrefix,
                            modifier = Modifier.bodyLMedium()
                                .sPaddingRight(6)
                                .styleModifier {
                                    property("white-space", "nowrap")
                                    property("line-height", "1")
                                    property("transform", "translateY(1px)")
                                }
                        )
                    }
                }
                SpanText(
                    selectedLabel ?: allLabel,
                    modifier = Modifier.bodyLMedium().color(sportColors.primary)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }

        // Dropdown
        if (expanded && useCityPanel) {
            com.arena_web.ui.components.dropdown.LocationDropdownPanel(
                selectedCity = selectedValue,
                cities = options,
                cityFlags = optionPrefixes
                    ?.let { prefixes -> options.indices.associate { options[it] to prefixes.getOrNull(it).orEmpty() } }
                    ?: emptyMap(),
                onSelect = { city ->
                    onSelected(city)
                    expanded = false
                },
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(52)
                    .sLeft(0)
                    .zIndex(100)
                    .styleModifier {
                        property("width", "max-content")
                        property("max-height", "260px")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                    }
            )
        } else if (expanded) {
            Column(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(52)
                    .sLeft(0)
                    .sMinWidth(200)
                    .sBorderRadius(16)
                    .sPadding(8)
                    .zIndex(100)
                    .styleModifier {
                        property("width", "max-content")
                        property("max-height", "260px")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                                "linear-gradient(0deg, var(--l-s3, #060E1E), var(--l-s3, #060E1E)) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                    }
            ) {
                // "All" option
                val allHovered = hoveredIndex == -2
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sBorderRadius(10)
                        .sPadding(10, 14)
                        .sPaddingRight(24)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            property(
                                "background",
                                when {
                                    selectedValue == null -> "rgba(0, 97, 255, 0.12)"
                                    allHovered -> "var(--l-hover, rgba(255, 255, 255, 0.06))"
                                    else -> "transparent"
                                }
                            )
                        }
                        .onMouseEnter { hoveredIndex = -2 }
                        .onMouseLeave { if (hoveredIndex == -2) hoveredIndex = -1 }
                        .onClick {
                            onSelected(null)
                            expanded = false
                        }
                ) {
                    SpanText(
                        allLabel,
                        modifier = Modifier.bodyLMedium().color(if (selectedValue == null) org.jetbrains.compose.web.css.Color(sportColors.primary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }

                options.forEachIndexed { index, option ->
                    val value = values?.getOrNull(index) ?: option
                    val isSelected = value == selectedValue
                    val isHovered = hoveredIndex == index
                    val prefix = optionPrefixes?.getOrNull(index).orEmpty()
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sBorderRadius(10)
                            .sPadding(10, 14)
                            .sPaddingRight(24)
                            .cursor(Cursor.Pointer)
                            .styleModifier {
                                property(
                                    "background",
                                    when {
                                        isSelected -> "rgba(0, 97, 255, 0.12)"
                                        isHovered -> "var(--l-hover, rgba(255, 255, 255, 0.06))"
                                        else -> "transparent"
                                    }
                                )
                            }
                            .onMouseEnter { hoveredIndex = index }
                            .onMouseLeave { if (hoveredIndex == index) hoveredIndex = -1 }
                            .onClick {
                                onSelected(value)
                                expanded = false
                            }
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(6.px)
                        ) {
                            if (prefix.isNotEmpty()) {
                                SpanText(
                                    prefix,
                                    modifier = Modifier.bodyLMedium()
                                        .styleModifier {
                                            property("white-space", "nowrap")
                                            property("line-height", "1")
                                            property("transform", "translateY(1px)")
                                        }
                                )
                            }
                            SpanText(
                                option,
                                modifier = Modifier.bodyLMedium().color(if (isSelected) org.jetbrains.compose.web.css.Color(sportColors.primary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2).styleModifier { property("white-space", "nowrap") }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FilterPillDate(
    label: String,
    selectedDate: String?,
    onDateSelected: (String?) -> Unit
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var expanded by remember { mutableStateOf(false) }

    // Format date for display
    val displayValue = if (selectedDate != null) {
        try {
            val parts = selectedDate.split("-")
            "${parts[2]}/${parts[1]}"
        } catch (_: Exception) { selectedDate }
    } else t.pageFilterAll

    // Same trick as FilterPillDropdown — `position:fixed` backdrop is trapped
    // by an ancestor `transform`, so we listen on document instead.
    val dropdownId = remember { "fpd-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(dropdownId)
            if (target != null && root != null && !root.contains(target)) {
                expanded = false
            }
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose {
            kotlinx.browser.document.removeEventListener("mousedown", handler)
        }
    }

    Box(modifier = Modifier.id(dropdownId).position(Position.Relative)) {
        // Pill trigger — Figma 9508:162431, h 44, px 22, py 10.
        Box(
            modifier = Modifier
                .sHeight(44)
                .sBorderRadius(100)
                .sPadding(10, 22)
                .cursor(Cursor.Pointer)
                .onClick { expanded = !expanded }
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property("background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--l-s2, #0B1628), var(--l-s2, #0B1628)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box")
                },
            contentAlignment = Alignment.Center
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "$label: ",
                    modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier { property("white-space", "nowrap") }
                )
                SpanText(
                    displayValue,
                    modifier = Modifier.bodyLMedium().color(sportColors.primary)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }

        // Date picker dropdown — a var(--l-s2, #09142d) rounded card matching the Location
        // dropdown (LocationDropdownPanel); the picker is rendered `bare` so the
        // card is the only surface.
        if (expanded) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(52)
                    .sRight(0)
                    .zIndex(100)
                    .sBorderRadius(20)
                    .sPadding(12, 12)
                    .styleModifier {
                        property("min-width", "max-content")
                        property("box-sizing", "border-box")
                        property("background", "var(--l-s2, #09142d)")
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                    }
                    .onClick { it.stopPropagation() }
            ) {
                DatePicker(
                    selectedDate = selectedDate ?: "",
                    onDateSelected = { date ->
                        onDateSelected(date)
                        expanded = false
                    },
                    bare = true,
                    // Desktop filter: classic 7-day window paged by the arrows
                    // (no scroll strip), so the dropdown always shows 7 across.
                    scrollable = false
                )
            }
        }
    }
}

@Composable
private fun AddEventButton(onClick: () -> Unit = {}) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .sSize(44)
                .sBorderRadius(100)
                .backgroundColor(sportColors.primary)
                .overflow(Overflow.Hidden)
                .zIndex(1),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = AppIcons.Add,
                modifier = Modifier.styleModifier {
                    property("filter", sportColors.iconFilterOnPrimary)
                }
            )
        }
        Box(
            modifier = Modifier
                .sWidth(7)
                .sHeight(16)
                .backgroundColor(sportColors.primary)
                .sMarginLeft(-4)
                .zIndex(0)
        )
        Box(
            modifier = Modifier
                .sHeight(44)
                .sBorderRadius(22)
                .backgroundColor(sportColors.primary)
                .sPadding(10, 24)
                .sMarginLeft(-4),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.pageAddEvent,
                modifier = Modifier
                    .bodyLMedium()
                    .color(sportColors.onPrimary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

@Composable
private fun AddClubButton(onClick: () -> Unit = {}) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .sSize(44)
                .sBorderRadius(100)
                .backgroundColor(sportColors.primary)
                .overflow(Overflow.Hidden)
                .zIndex(1),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = AppIcons.Add,
                modifier = Modifier.styleModifier {
                    property("filter", sportColors.iconFilterOnPrimary)
                }
            )
        }
        Box(
            modifier = Modifier
                .sWidth(7)
                .sHeight(16)
                .backgroundColor(sportColors.primary)
                .sMarginLeft(-4)
                .zIndex(0)
        )
        Box(
            modifier = Modifier
                .sHeight(44)
                .sBorderRadius(22)
                .backgroundColor(sportColors.primary)
                .sPadding(10, 24)
                .sMarginLeft(-4),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.pageAddClub,
                modifier = Modifier
                    .bodyLMedium()
                    .color(sportColors.onPrimary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

@Composable
private fun MapButton(onClick: () -> Unit = {}, showMap: Boolean = false) {
    val t = LocalAppStrings.current
    // Figma 8750:342078 — outlined pill, subtle Arena-blue gradient fill,
    // project gradient border. Doubles as the list⇄map toggle: in map mode a
    // grid glyph + "List"-style label reads "back to the list".
    Row(
        modifier = Modifier
            .sBorderRadius(28)
            .sPadding(10, 24)
            .sGap(8)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("box-sizing", "border-box")
            }
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center
    ) {
        // Icon tinted to the same muted nav blue as the label text
        // (IconDefaultFilter ≈ Text.Secondary #B4CCF6).
        Icon(
            src = if (showMap) AppIcons.GridFilled else AppIcons.Map,
            modifier = Modifier.sSize(18).styleModifier {
                property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
            }
        )
        SpanText(
            if (showMap) t.pageList else t.pageMap,
            modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}

// =============================================================================
// Event Grid
// =============================================================================

private const val EVENT_ROW_HEIGHT = "calc((100vh - 247 * var(--scale)) / 2)"
private const val EVENT_GRID_SCROLL_ID = "event-grid-scroll"
private const val EVENT_GRID_SCROLL_KEY = "arena_desktop_events_scroll_top"

@Composable
private fun EventGrid(
    events: List<ArenaEvent>,
    isLoadingMore: Boolean = false,
    onEventClick: (ArenaEvent) -> Unit = {},
    onLoadMore: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    val currentOnLoadMore by rememberUpdatedState(onLoadMore)
    var restoredScroll by remember { mutableStateOf(false) }

    // Restore the previous scroll once events are rendered (e.g. on return
    // from the event-detail page). Fires once per fresh mount with content.
    LaunchedEffect(events.isNotEmpty()) {
        if (restoredScroll || events.isEmpty()) return@LaunchedEffect
        val saved = kotlinx.browser.window.sessionStorage.getItem(EVENT_GRID_SCROLL_KEY)
            ?.toDoubleOrNull() ?: return@LaunchedEffect
        // Wait one frame so the cards are painted before we set scrollTop —
        // otherwise scrollHeight is still 0 and the assignment is clamped.
        kotlinx.browser.window.requestAnimationFrame {
            kotlinx.browser.document.getElementById(EVENT_GRID_SCROLL_ID)?.scrollTop = saved
            restoredScroll = true
        }
    }

    DisposableEffect(Unit) {
        val el = kotlinx.browser.document.getElementById(EVENT_GRID_SCROLL_ID)
            ?: return@DisposableEffect onDispose {}
        var savePending = false
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            val scrollTop = el.scrollTop
            val clientHeight = el.clientHeight
            val scrollHeight = el.scrollHeight
            if (scrollTop + clientHeight >= scrollHeight - 300) {
                currentOnLoadMore()
            }
            // Throttle the sessionStorage write to one per animation frame —
            // scroll fires per-pixel and synchronous writes would stutter
            // mid-scroll on low-end devices.
            if (!savePending) {
                savePending = true
                kotlinx.browser.window.requestAnimationFrame {
                    kotlinx.browser.window.sessionStorage.setItem(
                        EVENT_GRID_SCROLL_KEY,
                        el.scrollTop.toString()
                    )
                    savePending = false
                }
            }
        }
        el.addEventListener("scroll", listener)
        onDispose { el.removeEventListener("scroll", listener) }
    }

    Column(modifier = modifier.id(EVENT_GRID_SCROLL_ID).sGap(20)) {
        // Hero row — first 2 events + center tagline
        if (events.isNotEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .position(Position.Relative)
                    .styleModifier {
                        property("height", EVENT_ROW_HEIGHT)
                        property("flex-shrink", "0")
                    }
            ) {
                Row(
                    modifier = Modifier.fillMaxSize().sGap(20)
                ) {
                    // Left hero card
                    ArenaEventCard(
                        event = events[0],
                        modifier = Modifier
                            .fillMaxHeight()
                            .styleModifier { property("flex", "31 1 0px") },
                        onClick = { onEventClick(events[0]) }
                    )

                    // Center spacer for tagline
                    Box(modifier = Modifier.styleModifier { property("flex", "38 1 0px") })

                    // Right hero card (or empty spacer to keep proportions)
                    if (events.size >= 2) {
                        ArenaEventCard(
                            event = events[1],
                            modifier = Modifier
                                .fillMaxHeight()
                                .styleModifier { property("flex", "31 1 0px") },
                            onClick = { onEventClick(events[1]) }
                        )
                    } else {
                        Box(modifier = Modifier.styleModifier { property("flex", "31 1 0px") })
                    }
                }

                // Center tagline overlay
                if (events.size >= 2) {
                    Column(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .top(0.px).bottom(0.px)
                            .left(0.px).right(0.px)
                            .sPaddingTop(40)
                            .styleModifier { property("pointer-events", "none") },
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Top
                    ) {
                        SpanText(
                            t.pageOwnTheArena,
                            modifier = Modifier
                                .sFontSize(46)
                                .fontWeight(FontWeight.SemiBold)
                                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                                .styleModifier {
                                    property("line-height", "46px")
                                    property("letter-spacing", "0.46px")
                                    property("text-align", "center")
                                }
                        )
                        SpanText(
                            t.pageArenaBannerSubtitle,
                            modifier = Modifier
                                .sFontSize(22)
                                .fontWeight(FontWeight.Normal)
                                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                                .sPaddingTop(8)
                                .styleModifier {
                                    property("line-height", "26px")
                                    property("letter-spacing", "0.22px")
                                    property("text-align", "center")
                                }
                        )
                    }
                }
            }
        }

        // Regular rows — 4 columns, events from index 2 onward
        val remainingEvents = events.drop(2)
        remainingEvents.chunked(4).forEach { rowEvents ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(20)
                    .styleModifier {
                        property("height", EVENT_ROW_HEIGHT)
                        property("flex-shrink", "0")
                    }
            ) {
                rowEvents.forEach { event ->
                    ArenaEventCard(
                        event = event,
                        modifier = Modifier
                            .fillMaxHeight()
                            .styleModifier { property("flex", "1 1 0px") },
                        onClick = { onEventClick(event) }
                    )
                }
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().sPadding(20),
                contentAlignment = Alignment.Center
            ) {
                // Intentionally empty — spacer only, no loading text (user pref).
            }
        }
    }
}

// =============================================================================
// Empty State
// =============================================================================

@Composable
private fun EmptyEventsState(onAddEvent: () -> Unit = {}) {
    EventsEmptyState(onAddEvent = onAddEvent)
}

// =============================================================================
// Club Grid
// =============================================================================

private const val CLUB_ROW_HEIGHT = "calc((100vh - 247 * var(--scale)) / 2)"

/** Clubs rendered per list "page" — more mount on scroll (12 rows of 4). */
private const val CLUB_LIST_PAGE = 48
private const val CLUB_GRID_SCROLL_ID = "club-grid-scroll"

@Composable
internal fun ClubGrid(
    clubs: List<ClubDto>,
    isLoadingMore: Boolean = false,
    onClubClick: (ClubDto) -> Unit = {},
    onLoadMore: () -> Unit = {},
    userLat: Double? = null,
    userLng: Double? = null,
    selectedCity: String? = null,
    cityFlags: Map<String, String> = emptyMap(),
    sportName: String = "all",
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    val currentOnLoadMore by rememberUpdatedState(onLoadMore)
    DisposableEffect(Unit) {
        val el = kotlinx.browser.document.getElementById(CLUB_GRID_SCROLL_ID)
            ?: return@DisposableEffect onDispose {}
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            val scrollTop = el.scrollTop
            val clientHeight = el.clientHeight
            val scrollHeight = el.scrollHeight
            if (scrollTop + clientHeight >= scrollHeight - 300) {
                currentOnLoadMore()
            }
        }
        el.addEventListener("scroll", listener)
        onDispose { el.removeEventListener("scroll", listener) }
    }

    Column(modifier = modifier.id(CLUB_GRID_SCROLL_ID).sGap(20)) {
        // Hero row — first 2 clubs + center tagline
        if (clubs.isNotEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .position(Position.Relative)
                    .styleModifier {
                        property("height", CLUB_ROW_HEIGHT)
                        property("flex-shrink", "0")
                    }
            ) {
                Row(
                    modifier = Modifier.fillMaxSize().sGap(20)
                ) {
                    // Left hero card
                    ClubCard(
                        club = clubs[0],
                        modifier = Modifier
                            .fillMaxHeight()
                            .styleModifier { property("flex", "31 1 0px") },
                        userLat = userLat,
                        userLng = userLng,
                        selectedCity = selectedCity,
                        cityFlags = cityFlags,
                        sportName = sportName,
                        onClick = { onClubClick(clubs[0]) }
                    )

                    // Center spacer for tagline
                    Box(modifier = Modifier.styleModifier { property("flex", "38 1 0px") })

                    // Right hero card
                    if (clubs.size >= 2) {
                        ClubCard(
                            club = clubs[1],
                            modifier = Modifier
                                .fillMaxHeight()
                                .styleModifier { property("flex", "31 1 0px") },
                            userLat = userLat,
                            userLng = userLng,
                            selectedCity = selectedCity,
                            cityFlags = cityFlags,
                            sportName = sportName,
                            onClick = { onClubClick(clubs[1]) }
                        )
                    }
                }

                // Center tagline overlay
                if (clubs.size >= 2) {
                    Column(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .top(0.px).bottom(0.px)
                            .left(0.px).right(0.px)
                            .sPaddingTop(40)
                            .styleModifier { property("pointer-events", "none") },
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Top
                    ) {
                        SpanText(
                            t.pageOwnTheArena,
                            modifier = Modifier
                                .sFontSize(46)
                                .fontWeight(FontWeight.SemiBold)
                                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                                .styleModifier {
                                    property("line-height", "46px")
                                    property("letter-spacing", "0.46px")
                                    property("text-align", "center")
                                }
                        )
                        SpanText(
                            t.pageArenaBannerSubtitle,
                            modifier = Modifier
                                .sFontSize(22)
                                .fontWeight(FontWeight.Normal)
                                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                                .sPaddingTop(8)
                                .styleModifier {
                                    property("line-height", "26px")
                                    property("letter-spacing", "0.22px")
                                    property("text-align", "center")
                                }
                        )
                    }
                }
            }
        }

        // Regular rows — 4 columns, clubs from index 2 onward
        val remainingClubs = clubs.drop(2)
        remainingClubs.chunked(4).forEach { rowClubs ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(20)
                    .styleModifier {
                        property("height", CLUB_ROW_HEIGHT)
                        property("flex-shrink", "0")
                    }
            ) {
                rowClubs.forEach { club ->
                    ClubCard(
                        club = club,
                        modifier = Modifier
                            .fillMaxHeight()
                            .styleModifier { property("flex", "1 1 0px") },
                        userLat = userLat,
                        userLng = userLng,
                        selectedCity = selectedCity,
                        cityFlags = cityFlags,
                        sportName = sportName,
                        onClick = { onClubClick(club) }
                    )
                }
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().sPadding(20),
                contentAlignment = Alignment.Center
            ) {
                // Intentionally empty — spacer only, no loading text (user pref).
            }
        }
    }
}

// =============================================================================
// Club Card
// =============================================================================

@Composable
private fun ClubCard(
    club: ClubDto,
    modifier: Modifier = Modifier,
    userLat: Double? = null,
    userLng: Double? = null,
    selectedCity: String? = null,
    cityFlags: Map<String, String> = emptyMap(),
    sportName: String = "all",
    onClick: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    val imageUrl = club.mainPhotoUrl ?: "/icons/event_football_field.png"
    // Distance from the user when a city is picked, else the club's city
    // (street/address fallback when coords are missing).
    val locationText = clubLocationText(t, club, userLat, userLng, selectedCity)
    // Country flag replaces the geo pin in all-cities mode (mobile parity).
    // Local SVG asset (data-first from the club's country) for cross-platform parity.
    val flag = if (selectedCity == null) flagAssetUrl(clubCountryOf(club)) else ""
    // Sport chip only while browsing all sports / Other — a specific sport
    // filter makes it redundant on every card (mobile parity).
    val sportLabel = if (sportName == "all" || sportName == "other") clubSportLabel(club) else null

    Box(
        modifier = modifier
            .overflow(Overflow.Hidden)
            .sBorderRadius(16)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("background",
                    "url('$imageUrl') center/cover no-repeat")
            }
    ) {
        // Project gradient border — photo background rules out a plain
        // gradient border, so the mask-composite ring overlays it instead.
        com.arena_web.ui.sections.profile.edit.GradientBorderRing(radiusPx = 16, borderWidthPx = 0.5)
        // Bottom gradient overlay
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Absolute)
                .bottom(0.px).left(0.px).right(0.px)
                .styleModifier {
                    property("height", "40%")
                    property("background", "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 40%, transparent 100%)")
                }
        )

        // Rating badge — glass chip pinned top-left over the photo (mobile
        // MobileClubCard parity: black-50 pill, hairline border, brand-blue
        // star + rating). No Book button on club cards — mobile has none.
        if (club.averageRating > 0) {
            Row(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(10).sLeft(10)
                    .sBorderRadius(12)
                    .sPaddingLeft(8).sPaddingRight(8).sPaddingTop(3).sPaddingBottom(3)
                    .sGap(4)
                    .styleModifier {
                        property("background", "rgba(0,0,0,0.5)")
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Gradient border ring (translucent chip fill → plain border
                // can't be gradient).
                com.arena_web.ui.sections.profile.edit.GradientBorderRing(radiusPx = 12)
                Box(
                    modifier = Modifier.sSize(16).styleModifier {
                        property("background-color", "#0061FF")
                        property("mask-image", "url('${AppIcons.StarRating16}')")
                        property("-webkit-mask-image", "url('${AppIcons.StarRating16}')")
                        property("mask-size", "contain")
                        property("-webkit-mask-size", "contain")
                        property("mask-repeat", "no-repeat")
                        property("-webkit-mask-repeat", "no-repeat")
                        property("mask-position", "center")
                        property("-webkit-mask-position", "center")
                    }
                )
                SpanText(
                    club.averageRating.asDynamic().toFixed(1) as String,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Medium)
                        .color(KobwebColor.hex("B4CCF6"))
                )
            }
        }

        // Bottom content: club name + location over the legibility fade
        // (mobile MobileClubCard parity — no action buttons on the card).
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Absolute)
                .bottom(0.px).left(0.px).right(0.px)
                .sPadding(18)
                .sGap(4)
        ) {
            SpanText(
                club.name,
                modifier = Modifier
                    .sFontSize(26)
                    .fontWeight(FontWeight.SemiBold)
                    .color(AppColors.PrimaryWhite)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
            )
            Row(
                modifier = Modifier.sGap(4),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (flag.isNotBlank()) {
                    // Local flag SVG URL — emoji-style 4:3 rounded-rect badge
                    // in place of the geo pin.
                    Box(
                        modifier = Modifier
                            .sWidth(16)
                            .sHeight(12)
                            .styleModifier {
                                property("background-image", "url(\"$flag\")")
                                property("background-size", "cover")
                                property("background-position", "center")
                                property("border-radius", "2px")
                            }
                    )
                } else {
                    Icon(
                        src = AppIcons.Location,
                        modifier = Modifier.sSize(16)
                    )
                }
                SpanText(
                    locationText,
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(FontWeight.Normal)
                        .color(KobwebColor.hex("B4CCF6"))
                        .styleModifier {
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                )
                if (sportLabel != null) {
                    Box(
                        modifier = Modifier
                            .sMarginLeft(8).sMarginRight(8)
                            .sWidth(1).sHeight(14)
                            .styleModifier {
                                property("background", "rgba(255,255,255,0.4)")
                                property("flex-shrink", "0")
                            }
                    )
                    SpanText(
                        sportLabel,
                        modifier = Modifier
                            .sFontSize(18)
                            .fontWeight(FontWeight.Normal)
                            .color(KobwebColor.hex("B4CCF6"))
                            .styleModifier { property("white-space", "nowrap") }
                    )
                }
            }
        }
    }
}

// =============================================================================
// Empty Clubs State
// =============================================================================

@Composable
private fun EmptyClubsState() {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier.sGap(24),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier = Modifier
                    .sSize(96)
                    .sBorderRadius(100)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 27%, rgba(3, 87, 230, 0.12) 68%) padding-box, " +
                                "linear-gradient(0deg, var(--l-s3, #060E1E), var(--l-s3, #060E1E)) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    },
                contentAlignment = Alignment.Center
            ) {
                Icon(src = AppIcons.Location, modifier = Modifier.sSize(40))
            }

            Column(
                modifier = Modifier.sGap(8),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    t.pageNoClubsYet,
                    modifier = Modifier
                        .sFontSize(22)
                        .fontWeight(FontWeight.SemiBold)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                )
                SpanText(
                    t.pageClubsWillAppear,
                    modifier = Modifier.bodyLRegular().color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }
    }
}

// =============================================================================
// Background Map (in-page map mode — mobile ArenaTabScreen parity)
// =============================================================================

private const val ARENA_BG_MAP_CONTAINER_ID = "arena-bg-map"

/**
 * Full-bleed interactive Leaflet map rendered as a background layer behind the
 * Arena action bar (mobile ArenaTabScreen's map mode). Shows pins for the
 * ACTIVE tab only — events on the Events tab, clubs on the Clubs tab. Tapping a
 * pin opens its details; picking a city pans the camera. Reuses the same dark
 * tiles / clustering / info-card pins as the (now unused) MapPopup.
 */
@Composable
private fun ArenaBackgroundMap(
    activeTab: String,
    events: List<ArenaEvent>,
    clubs: List<ClubDto>,
    userLat: Double? = null,
    userLng: Double? = null,
    selectedCity: String? = null,
    /** City → country-flag emoji, prefixes the club city in all-cities mode. */
    cityFlags: Map<String, String> = emptyMap(),
    /** Active sport filter — the sport chip shows only for "all"/"other". */
    sportName: String = "all",
    /** Mobile: stretch past the page layout's 16-px horizontal gutter so the
     *  map paints edge-to-edge. */
    fullBleed: Boolean = false,
    /** Desktop: extend the map UP under the top bar (30px page padding +
     *  63px header) — the top fade gradient dissolves it there, so the bar
     *  reads on a clean dark backdrop while the map runs full height. */
    underTopBar: Boolean = false,
    /** Phones: cull markers to the viewport (capped at the 250 nearest) —
     *  building the full catalog stalls a phone's main thread for seconds.
     *  Desktop keeps the default and renders everything. */
    cullMarkers: Boolean = false,
    onEventOpen: (ArenaEvent) -> Unit = {},
    onClubOpen: (ClubDto) -> Unit = {}
) {
    val t = LocalAppStrings.current

    // Markers for the active tab only (mobile parity).
    val markersJson = remember(activeTab, events, clubs, userLat, userLng, selectedCity, cityFlags, sportName, t) {
        val arr = buildJsonArray {
            if (activeTab == "Events") {
                events.forEach { event ->
                    if (event.latitude.isNotBlank() && event.longitude.isNotBlank()) {
                        add(buildJsonObject {
                            put("id", event.id)
                            put("lat", event.latitude)
                            put("lng", event.longitude)
                            put("datetime", "${event.date} • ${event.time}")
                            put("title", event.title.ifBlank { event.sportLabel })
                            put("subtitle", event.location)
                            put("image", event.imageUrl)
                            mapMembersText(t, event)?.let { put("members", it) }
                            event.cost?.let { put("cost", it) }
                        })
                    }
                }
            } else {
                clubs.forEach { club ->
                    if (club.latitude != null && club.longitude != null) {
                        add(buildJsonObject {
                            put("id", "club:${club.id}")
                            put("club", true)
                            put("lat", club.latitude.toString())
                            put("lng", club.longitude.toString())
                            put("title", club.name)
                            // Same location logic as the list card (mobile
                            // parity): distance from the user when a city is
                            // picked, else the club's city — prefixed with its
                            // country flag in all-cities mode.
                            val locText = clubLocationText(t, club, userLat, userLng, selectedCity)
                            val flag = if (selectedCity == null) {
                                countryCodeToFlagEmoji(clubCountryOf(club))
                            } else ""
                            put("subtitle", if (flag.isNotBlank()) "$flag $locText" else locText)
                            // Sport chip only while browsing all sports /
                            // Other — a specific sport filter makes it
                            // redundant on every card (mobile parity).
                            if (sportName == "all" || sportName == "other") {
                                put("sport", clubSportLabel(club))
                            }
                            put("rating", club.averageRating.toString())
                            if (club.mainPhotoUrl != null) {
                                put("image", club.mainPhotoUrl)
                            }
                        })
                    }
                }
            }
        }
        Json.encodeToString(arr)
    }

    // Geocode the selected city so the map can centre on it (cached per name).
    val cityCoords = remember { mutableStateOf<Pair<Double, Double>?>(null) }
    LaunchedEffect(selectedCity) {
        val city = selectedCity
        if (city == null) { cityCoords.value = null; return@LaunchedEffect }
        cityCoords.value = geocodeCityCached(city)
    }

    // Init map + markers. Tapping any pin opens its details directly.
    // markersJson changes several times while the page settles (cached catalog
    // → background sync, geolocation arriving, city flags resolving) — the map
    // itself is created ONCE and refreshes only swap the markers: re-running
    // initReadOnly tore down and rebuilt the whole MapLibre WebGL basemap on
    // every change, seconds of frozen main thread per pass on phones.
    LaunchedEffect(markersJson) {
        val firstInit = !LeafletMap.isInitialized()
        if (firstInit) {
            kotlinx.coroutines.delay(100)
            LeafletMap.initReadOnly(ARENA_BG_MAP_CONTAINER_ID, 46.0, 15.0, 4)
        }
        // No clearMarkers() on refresh: addMarkers diffs the live markers
        // against the new item set by key, so unchanged pins stay put instead
        // of blinking through a clear-and-rebuild.
        LeafletMap.addMarkers(markersJson, cull = cullMarkers)
        LeafletMap.onMarkerOpen { id ->
            if (id.startsWith("club:")) {
                val clubId = id.removePrefix("club:")
                clubs.firstOrNull { it.id == clubId }?.let(onClubOpen)
            } else {
                events.firstOrNull { it.id == id }?.let(onEventOpen)
            }
        }
        if (firstInit) {
            // City zoom 14 = close street level, past disableClusteringAtZoom
            // (13), so the city's pins show individually right away, not as
            // clusters. AFTER addMarkers — its one-time fitBounds must not
            // override the city centre. Only on creation: a marker refresh
            // keeps the camera wherever the user panned it (the
            // LaunchedEffect(cityCoords) below handles actual city changes).
            cityCoords.value?.let { (lat, lng) -> LeafletMap.panTo(lat, lng, 14) }
        }
    }

    // Pan to the selected city when it (re)resolves while the map is open.
    LaunchedEffect(cityCoords.value) {
        // City zoom 14 = close street level, past disableClusteringAtZoom (13),
        // so the city's pins show individually right away, not as clusters.
        cityCoords.value?.let { (lat, lng) -> LeafletMap.panTo(lat, lng, 14) }
    }

    // Tear the map down when leaving map mode so re-entry rebuilds cleanly.
    DisposableEffect(Unit) {
        onDispose { LeafletMap.destroy() }
    }

    // Full-bleed map container behind the action bar. A subtle top/bottom
    // gradient fade blends the map into the page background (mobile parity).
    // z-index 0 keeps this the BOTTOM layer — Leaflet's panes use high internal
    // z-indexes, so the action bar + floating buttons above must sit in a
    // higher-stacked sibling to stay visible and clickable.
    Box(
        modifier = Modifier
            .fillMaxSize()
            .position(Position.Absolute)
            .bottom(0.px)
            .left(if (fullBleed) (-16).px else 0.px)
            .right(if (fullBleed) (-16).px else 0.px)
            .zIndex(0)
            // fillMaxSize's width/height:100% win over the right/bottom insets
            // (absolute positioning ignores them once a size is set), so the
            // full-bleed variants must widen/heighten explicitly.
            .thenIf(fullBleed) {
                Modifier.styleModifier { property("width", "calc(100% + 32px)") }
            }
            .styleModifier {
                if (underTopBar) {
                    // 30px page top padding + 63px header (both --scale'd).
                    property("top", s(-93))
                    property("height", "calc(100% + ${s(93)})")
                } else {
                    property("top", "0")
                }
            }
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .id(ARENA_BG_MAP_CONTAINER_ID)
                // Dark navy fill so any area not yet covered by tiles (or beyond
                // the world bounds when zoomed out) reads dark, not white.
                .backgroundColor(KobwebColor.hex("0E1626"))
        )
        // NOTE: the old 50%-navy wash overlay is gone — the basemap itself now
        // carries the exact mobile navy palette (vector MapLibre style in
        // LeafletMap.initReadOnly), so a wash would only mute it again.
        // Top + bottom fade into the app body/top-bar background so the map
        // dissolves under the top bar and above the bottom edge with no hard
        // line. The start colour must match what list mode shows under the top
        // bar: the body COMPOSITE — var(--l-s3, #02040B) base with the blue tint
        // rgba(60,121,241,0.072) at full strength near the viewport top (see
        // the body background in AppTheme). So the fade is the same two-layer
        // stack — the blue tint over the dark base — with both layers easing
        // out to transparent by ~14% on the same curve (tint alphas = 0.072 ×
        // the dark ladder). The bottom edge fades to plain var(--l-s3, #02040B) (tint is
        // zero at the viewport bottom). Draw only; pointer-events off so it
        // never eats map taps.
        // z-index 1200 is REQUIRED: the map container is not a stacking
        // context, so Leaflet's internal panes (tile 200 … popup 700, control
        // 800) escape into this parent stack and paint over a z:auto sibling —
        // being a later sibling is not enough. 1200 clears every pane; page UI
        // lives in the z:1 sibling stack above this whole z:0 layer anyway.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .position(Position.Absolute)
                .top(0.px).left(0.px).right(0.px).bottom(0.px)
                .zIndex(1200)
                .styleModifier {
                    property("pointer-events", "none")
                    property(
                        "background",
                        "linear-gradient(180deg, " +
                            "rgba(60,121,241,0.072) 0%, " +
                            "rgba(60,121,241,0.07) 2%, " +
                            "rgba(60,121,241,0.063) 4.5%, " +
                            "rgba(60,121,241,0.052) 7%, " +
                            "rgba(60,121,241,0.037) 9%, " +
                            "rgba(60,121,241,0.023) 10.5%, " +
                            "rgba(60,121,241,0.011) 12%, " +
                            "transparent 14%), " +
                        "linear-gradient(180deg, " +
                            "var(--l-s3, #02040B) 0%, " +
                            "rgba(2,4,11,0.97) 2%, " +
                            "rgba(2,4,11,0.88) 4.5%, " +
                            "rgba(2,4,11,0.72) 7%, " +
                            "rgba(2,4,11,0.52) 9%, " +
                            "rgba(2,4,11,0.32) 10.5%, " +
                            "rgba(2,4,11,0.15) 12%, " +
                            "transparent 14%, " +
                            "transparent 90%, " +
                            "rgba(2,4,11,0.9) 100%)"
                    )
                }
        )
    }
}


// =============================================================================
// Club helpers (mirror the mobile ArenaCards helpers)
// =============================================================================

// Per-name geocode cache so panning to a repeated city doesn't re-hit Nominatim.
private val cityGeocodeCache = mutableMapOf<String, Pair<Double, Double>?>()

/** Forward-geocode a city name to coordinates (reuses the shared Nominatim
 *  city search — biased to the user's country then global). Cached per name. */
internal suspend fun geocodeCityCached(city: String): Pair<Double, Double>? {
    cityGeocodeCache[city]?.let { return it }
    if (cityGeocodeCache.containsKey(city)) return null
    val result = searchCities(city).firstOrNull()?.let { it.lat to it.lng }
    cityGeocodeCache[city] = result
    return result
}

/** "Tennis" / "Tennis · Padel" / custom sport name — the meta chip after the
 *  city divider. Multi-sport clubs list every sport, primary first. */
internal fun clubSportLabel(club: ClubDto): String {
    club.customSportName?.takeIf { it.isNotBlank() }?.let { return it }
    val pretty = { t: String -> t.lowercase().replaceFirstChar { it.uppercase() } }
    val all = (listOf(club.type) + club.types).distinct()
    return all.joinToString(" · ") { pretty(it) }
}

/**
 * City segment of an address — NOT the country:
 *  - "Calle Aduana 21, Madrid" → "Madrid"
 *  - "Calle Aduana 21, Madrid, Spain" → "Madrid" (second-to-last segment)
 *  - "Calle Aduana, 21, 28013 Madrid, España" → "Madrid" (postcode stripped)
 */
internal fun clubCity(address: String): String {
    val parts = address.split(',').map { it.trim() }.filter { it.isNotEmpty() }
    // Prefer the segment carrying the 5-digit postcode ("08908 L'Hospitalet de
    // Llobregat") — the text after the postcode is the real municipality, even
    // when the club is grouped under a different province for the city filter.
    parts.firstOrNull { Regex("^\\d{5}\\b").containsMatchIn(it) }?.let { seg ->
        val city = seg.replaceFirst(Regex("^\\d{5}"), "").trim()
        if (city.isNotEmpty()) return city
    }
    val candidate = when {
        parts.size >= 3 -> parts[parts.size - 2]
        parts.isNotEmpty() -> parts.last()
        else -> address.trim()
    }
    return candidate.dropWhile { it.isDigit() || it.isWhitespace() }.trim()
}

/** Spanish province by postcode prefix (first two digits) — the grouping key
 *  for the city filter. A club in a metro suburb (e.g. 08908 L'Hospitalet)
 *  groups under its province capital ("Barcelona") while its address keeps the
 *  real municipality. */
private val SPANISH_PROVINCES = mapOf(
    "01" to "Álava", "02" to "Albacete", "03" to "Alicante", "04" to "Almería",
    "05" to "Ávila", "06" to "Badajoz", "07" to "Baleares", "08" to "Barcelona",
    "09" to "Burgos", "10" to "Cáceres", "11" to "Cádiz", "12" to "Castellón",
    "13" to "Ciudad Real", "14" to "Córdoba", "15" to "A Coruña", "16" to "Cuenca",
    "17" to "Girona", "18" to "Granada", "19" to "Guadalajara", "20" to "Gipuzkoa",
    "21" to "Huelva", "22" to "Huesca", "23" to "Jaén", "24" to "León",
    "25" to "Lleida", "26" to "La Rioja", "27" to "Lugo", "28" to "Madrid",
    "29" to "Málaga", "30" to "Murcia", "31" to "Navarra", "32" to "Ourense",
    "33" to "Asturias", "34" to "Palencia", "35" to "Las Palmas", "36" to "Pontevedra",
    "37" to "Salamanca", "38" to "Santa Cruz de Tenerife", "39" to "Cantabria",
    "40" to "Segovia", "41" to "Sevilla", "42" to "Soria", "43" to "Tarragona",
    "44" to "Teruel", "45" to "Toledo", "46" to "Valencia", "47" to "Valladolid",
    "48" to "Bizkaia", "49" to "Zamora", "50" to "Zaragoza", "51" to "Ceuta",
    "52" to "Melilla",
)

/** Country name (address tail) → ISO code, for the flag. */
private val COUNTRY_NAME_TO_CODE = mapOf(
    "spain" to "es", "españa" to "es", "espagne" to "es",
    "ukraine" to "ua", "україна" to "ua", "украина" to "ua",
    "portugal" to "pt", "france" to "fr", "italy" to "it", "italia" to "it",
    "germany" to "de", "deutschland" to "de", "poland" to "pl", "polska" to "pl",
)

private fun addressPostcodePrefix(address: String): String? =
    Regex("\\b(\\d{5})\\b").find(address)?.groupValues?.get(1)?.take(2)

/**
 * Grouping key for the city filter: the club's Spanish province (from its
 * postcode) so metro suburbs fold into their capital; falls back to the parsed
 * city when no Spanish postcode is present.
 */
internal fun clubProvince(address: String): String {
    // Only fold into a Spanish province when the club is actually in Spain —
    // Ukrainian (and other) 5-digit postcodes share Spain's 2-digit prefixes
    // (e.g. Ternopil 46xxx would otherwise map to "Valencia").
    if (clubCountryCode(address) == "es") {
        addressPostcodePrefix(address)?.let { SPANISH_PROVINCES[it]?.let { p -> return p } }
    }
    return clubCity(address)
}

/**
 * ISO country code derived from the club's own address — no geocoding. Reads the
 * trailing country name, then falls back to a Spanish postcode prefix → "es".
 * Returns null when the country can't be determined.
 */
internal fun clubCountryCode(address: String): String? {
    val parts = address.split(',').map { it.trim() }.filter { it.isNotEmpty() }
    parts.lastOrNull()?.lowercase()?.let { COUNTRY_NAME_TO_CODE[it]?.let { c -> return c } }
    addressPostcodePrefix(address)?.let { if (it in SPANISH_PROVINCES) return "es" }
    return null
}

/** Grouping label for a club — server `city` when present (no parsing), else
 *  derived from the address. Data-first so multi-country stays collision-free. */
internal fun clubGroupCity(club: ClubDto): String =
    club.city?.takeIf { it.isNotBlank() } ?: clubProvince(club.address)

/** ISO country code for a club — server `country` when present, else derived. */
internal fun clubCountryOf(club: ClubDto): String? =
    club.country?.takeIf { it.isNotBlank() }?.lowercase() ?: clubCountryCode(club.address)

/** Great-circle distance, km — null when either point is unknown. */
internal fun clubDistanceKm(lat1: Double?, lon1: Double?, lat2: Double?, lon2: Double?): Double? {
    if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null
    val dLat = (lat2 - lat1) * PI / 180
    val dLon = (lon2 - lon1) * PI / 180
    val a = sin(dLat / 2).pow(2) + cos(lat1 * PI / 180) * cos(lat2 * PI / 180) * sin(dLon / 2).pow(2)
    return 2 * 6371.0 * atan2(sqrt(a), sqrt(1 - a))
}

/** "2.5" under 10 km, whole km above — same rounding as the mobile club chip. */
internal fun formatClubKm(km: Double): String =
    if (km >= 10) km.roundToInt().toString() else ((km * 10).roundToInt() / 10.0).toString()

/**
 * Location line for a club card / pin: when a specific city is selected show
 * the distance from the user ("<n> km"); when browsing all cities show the
 * club's city; fall back to the street/address when coords are missing.
 */
internal fun clubLocationText(
    t: AppStrings,
    club: ClubDto,
    userLat: Double?,
    userLng: Double?,
    selectedCity: String?,
): String {
    val cityBrowsing = selectedCity == null
    if (!cityBrowsing) {
        val km = clubDistanceKm(userLat, userLng, club.latitude, club.longitude)
        if (km != null) return t.clubDistanceKm(formatClubKm(km))
    }
    return clubCity(club.address).ifBlank { club.address }
}

private fun mapMembersText(t: AppStrings, event: ArenaEvent): String? = when {
    event.membersJoined != null && event.membersTotal != null ->
        t.arenaMembersJoinedTotal(event.membersJoined, event.membersTotal)
    event.membersJoined != null -> t.arenaMembersCount(event.membersJoined)
    event.membersTotal != null -> t.arenaMembersCount(event.membersTotal)
    else -> null
}
